/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { decode, logError } from '@modernloop/shared/utils';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import WorkdayCard, { Props as WorkdayProps } from 'src/views-new/Settings/Integrations/Cards/WorkdayCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, { ExtensionSidebarParsedUrlType, ParsedAtsUrlResult, ScorecardGroupByStrategy } from './AtsService';

const CANDIDATE_REFRENCE_REGEX = /\(([^()\s]*\d{3,}[^()\s]*)\)/;
/**
 * Private function always returns an object
 * @param extraData
 * @returns {}
 */
const getRefrenceIds = (
  extraData: string
): {
  candidateRefrenceId: string;
  jobRefrenceId: string;
} => {
  const data = {
    candidateRefrenceId: '',
    jobRefrenceId: '',
  };

  if (!extraData) {
    return data;
  }

  /**
   * Chrome extesnion version 2.0.7 & higher
   */
  let extraDataDecodedString = '';
  try {
    extraDataDecodedString = decode(extraData);
  } catch {
    /**
     * Chrome extesnion version 2.0.6 only is sending extraData as encodeURIComponent
     */
    extraDataDecodedString = decodeURIComponent(extraData);
  }

  if (!extraDataDecodedString) {
    return data;
  }

  const extraDataJson = JSON.parse(extraDataDecodedString);

  if (extraDataJson?.workdayCandidateTitle) {
    const [, candidateRefrenceId] = extraDataJson.workdayCandidateTitle.match(CANDIDATE_REFRENCE_REGEX) || [];
    if (candidateRefrenceId) {
      data.candidateRefrenceId = candidateRefrenceId.trim();
    }
  }

  if (extraDataJson?.workdayJobTitle) {
    const words = extraDataJson.workdayJobTitle.split(/\s+/);
    const jobRefrenceId = words?.length >= 2 ? words[1] : '';
    if (jobRefrenceId) {
      data.jobRefrenceId = jobRefrenceId.trim();
    }
  }

  return data;
};

/* *
 * Workday ATS service
 * Imp: Never have atsType set in ATS service
 * */
class WorkdayAtsService implements AtsSevice {
  readonly featureFlag = null;

  readonly name = 'Workday';

  readonly hostname = 'workday'; // ex. https://impl.workday.com/zktechnology_pt1/d/pex/home.htmld

  readonly logoUrl = `/static/images/integrations/workday.png`;

  readonly helpCenterUrl = HelpCenterUrl.WORKDAY_INTEGRATION_WEB_SERVICES;

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.questionnaires';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noQuestionnaire';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-nav:nav.scorecards.selectTitle';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-nav:nav.scorecards.searchPlaceholder';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-nav:nav.scorecards.searchPlaceholderEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = false;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = false;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AtsJobStage;

  readonly scorecardSortKey = null;

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = null;

  readonly showRequisitionId = false;

  readonly canSupportBrightHire = true;

  readonly hasScorecardSync = false;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAtsInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = true;

  readonly hasEnforcedJobPermissions = false;

  readonly resetInterviewPlanWarningMessage =
    'The interview plan for this stage will be reset to its current settings in Workday.';

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (props: WorkdayProps, integrations: ListIntegrationResponse): JSX.Element => {
    return <WorkdayCard {...props} workday={integrations?.workday} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string, extraData: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      // check if url is workday
      if (!url.hostname.includes(this.hostname) || !extraData) {
        return null; // exit early
      }

      const { candidateRefrenceId, jobRefrenceId } = getRefrenceIds(extraData);

      if (candidateRefrenceId) {
        return {
          type: ExtensionSidebarParsedUrlType.Candidate,
          atsCandidateId: candidateRefrenceId,
          atsJobId: jobRefrenceId,
          atsApplicationId: null,
          isRefrenceId: true,
        };
      }
    } catch (error) {
      logError('Error parsing Workday ATS url', error);
    }

    return null;
  };
}

export default new WorkdayAtsService();
